import React, { useRef, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { matchRoutes } from "react-router-config";
import { connect } from "react-redux";

import { asideRepositoryNavigationByModule } from "../Repository/navigation";

import styles from "./Repository.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getDownloadTemplateData,
    deleteGeneratorFileEntryAction,
    forceGenerateFileAction,
} from "../../store/actions";

import proxy from "../../routes/proxy";

import { changeDeepObject } from "../../utils";
import {
    redirectNotValid,
    buildStaticLinks,
    setAccount,
    applyThemeWithPriority,
} from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import {
    controllerQueries,
    generateChildren,
    getQueryResultsArray,
    onClickConfiguratorRepository,
    repoRedirectValidations,
    GlobalPageLayout,
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    buildDisplayComponents,
    buildHeaderComponents,
    headerTitle,
    getDownloadableQueries,
    generateNavigation,
    buildNavProps,
} from "../utils/utils";

const RepositoryComponent = (props) => {
    const {
        pushModal,
        modules,
        fetch,
        menu,
        match,
        route,
        location,
        history,
        reload,
        matchParams,
        projectId,
        modulePrefix,
        locale,
        fetchResults,
        fetchStatus,
        appContext,
        auth,
        userInformation,
        i18n,
    } = useControllerInitialization(props);

    // Remove viewMode from URL if present (Repository.js doesn't use new_navigation)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has("viewMode")) {
            searchParams.delete("viewMode");
            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            history.replace(newUrl);
        }
    }, [location.search, location.pathname, history]);

    let { fiscalYear, importId, section, fileType } = matchParams;
    const filesList = useRef([]);

    const {
        menuOrganizationProject,
        organizationProjects,
        queryStatus,
        projectFiscalYears,
        project,
        fiscalYears,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: repositoryQueryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: RepositoryComponent,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const { loading = true, success = false } = repositoryQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "Repository");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    //############ Build Generic Menu  ############
    const { navData, importIdToUse, routeQueries, params, firstModuleFromProject } =
        buildGenericMenu(
            modules,
            fetchResults,
            locale,
            fiscalYear,
            projectId,
            importId,
            project,
            location,
            matchParams,
            route,
        );

    const {
        repositoryStatus = { status: 1 },
        getTotalExportLines = "",
        genericOverview,
    } = getQueryResultsArray(fetchResults, [
        {
            resultName: "repositoryStatus",
            queryName: `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}`,
        },
        {
            resultName: "getTotalExportLines",
            queryName: `${locale}_get_limit_export`,
        },
        {
            resultName: "genericOverview",
            queryName: `${locale}_generic_overview_fiscalYear_${fiscalYear}_importId_${importId}_locale_${locale}_modulePrefix_${modulePrefix}_projectId_${projectId}`,
        },
    ]);

    const getLayout = JSON.parse(genericOverview || "[]");

    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears },
    });

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const displayComponents = buildDisplayComponents(route, getLayout);

    const children = generateChildren({
        displayComponents,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        modules,
        fetch,
        pushModal,
        reload,
        onClickConfigurator: onClickConfiguratorRepository,
    });

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: RepositoryComponent,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    // function to make the correct redirects in the repository
    const repositoryRedirect = repoRedirectValidations({
        redirect,
        fiscalYear,
        importId,
        fiscalYears,
        appContext,
        match,
        matchParams,
        importIdToUse,
        firstModuleFromProject,
        section,
        fileType,
        queryStatus,
        setAccount,
        project,
        locale,
        history,
        proxy,
        projectId,
    });

    if (repositoryRedirect) {
        return repositoryRedirect;
    }

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects,
    };

    return (
        LoadingComponent || (
            <GlobalPageLayout
                styles={styles}
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    filesList,
                    reportsAction: true,
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: appContext === "repository" ? "" : importIdsFromUrlFiscalYear,
                    downloadableQueries,
                    title: title ? i18n`${title}` : i18n`${params.section || ""}`,
                    totalLinesCsv: getTotalExportLines,
                    headerComponents: headerComponents,
                    titleBarActions:
                        headerComponents.length > 0
                            ? headerComponents.map((headerComponent, index) => (
                                <headerComponent.Component
                                    key={`titlebar-action-${index}`}
                                    filesList={filesList}
                                    pushModal={pushModal}
                                    i18n={i18n}
                                    uploadFiles={uploadAction}
                                    reload={reload}
                                    projectId={projectId}
                                    user={userInformation}
                                    endPointToRequest={
                                        route?.headerComponentConfig?.endPointToRequest || ""
                                    }
                                    {...headerComponent.props}
                                />
                            ))
                            : undefined,
                }}
                headerComponentsMapper={() => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        key="generation"
                        filesList={filesList}
                        pushModal={pushModal}
                        i18n={i18n}
                        uploadFiles={uploadAction}
                        reload={reload}
                        projectId={projectId}
                        user={userInformation}
                        endPointToRequest={route?.headerComponentConfig?.endPointToRequest || ""}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={fetch}
                route={route}
                matchParams={matchParams}
                menu={menu}
                // This controller does not need to have isPageLoading
                isLoadingContent={() => false}
            >
                {children}
            </GlobalPageLayout>
        )
    );
};

RepositoryComponent.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteGeneratorFileEntry: deleteGeneratorFileEntryAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getDownloadTemplateData,
        forceGenerateFile: forceGenerateFileAction,
    },
)(withRouter(RepositoryComponent));